import { CartItemsV3Query, CartSingleItemsQuery } from '~/__generated__/app/query/Cart/Cart.graphql'
import { DomesticBusCartItemOption } from '~/__generated__/__types__'
import { groupBy, isEmpty, isNil, omit, pick, pipe } from 'ramda'

const convertCategory = (item: CartItemsV3Query['cartItemsV3'][number]) => {
  if (item.__typename === 'DomesticAccommodationCartItem') {
    return item.options
      .filter(option => option.isChosen)
      .map(option => {
        return {
          type: 'property',
          propertyId: item.propertyId,
          roomTypeId: option.roomTypeId,
          useType: option.useType === 'DAY_USE' ? 'R' : option.useType === 'OVER_NIGHT' ? 'S' : 'D',
          ratePlanId: option.ratePlanId || undefined,
          ratePlanVersion: option.ratePlanVersion || undefined,
          checkInDate: option.checkInDate,
          checkOutDate: option.checkOutDate,
          checkInAt: option.checkInAt,
          checkOutAt: option.checkOutAt,
          surchargeIds: option.surcharges || undefined,
          adults: option.adults,
          childrenAges: option.child || undefined,
        }
      })
  } else if (item.__typename === 'DomesticLeisureCartItem') {
    item.options = item.options.filter(option => option.isChosen)
    if (item.options.length <= 0) {
      return
    }
    return item.options.map(option => ({
      type: 'leisure',
      id: item.dealId,
      productVersion: option.productVersion,
      options: [
        {
          id: option.leisureOptionId,
          quantity: option.quantity,
          date: option?.date,
          time: option?.time,
        },
      ],
    }))
  } else if (item.__typename === 'DomesticTrainCartItem') {
    return item.options
      .filter(option => option.isChosen)
      .map(option => {
        const single = option.trains[0]
        const returns = option.trains[1]
        return {
          type: 'train',
          adults: option.adults,
          child: option.child,
          single: {
            trainNo: single.trainNo,
            goodsNo: single.goodsNo,
            departureStationCode: single.departureStationCode,
            runningDate: single.runningDate,
            arrivalStationCode: single.arrivalStationCode,
            carriageClassCode: single.carriageClassCode,
            seats: (single.seats || []).map(seat => `${seat.carriageNo}:${seat.seatNo}`),
          },
          returns: returns
            ? {
                trainNo: returns.trainNo,
                goodsNo: returns.goodsNo,
                departureStationCode: returns.departureStationCode,
                runningDate: returns.runningDate,
                arrivalStationCode: returns.arrivalStationCode,
                carriageClassCode: returns.carriageClassCode,
                seats: (returns.seats || []).map(seat => `${seat.carriageNo}:${seat.seatNo}`),
              }
            : undefined,
        }
      })
  } else if (item.__typename === 'DomesticBusCartItem') {
    const byReturnTrip = groupBy<ReturnType<typeof convertReservation4Pay>>(reservation => {
      if (reservation.returnTrip) return 'returns'
      return 'going'
    })
    const removeEmpty = item => {
      if (isEmpty(item.going) || isNil(item.going)) delete item.going
      if (isEmpty(item.returns) || isNil(item.returns)) delete item.returns
      return item
    }
    const removeReturnTripAndFirst = item => ({
      going: omit(['returnTrip'], item.going?.[0]),
      returns: omit(['returnTrip'], item.returns?.[0]),
    })

    const toBusType = pipe(byReturnTrip, removeReturnTripAndFirst, removeEmpty)
    const convertReservation4Pay = reservation => ({
      returnTrip: reservation.returnTrip,
      allocationArrivalTerminalNo: reservation.allocationArrivalTerminalNo,
      allocationDepartureTerminalNo: reservation.allocationDepartureTerminalNo,
      allocationDepartureTime: reservation.allocationDepartureTime,
      arrivalTerminalNo: reservation.arrivalTerminalNo,
      busGrade: reservation.busGrade,
      departureDate: reservation.departureDate,
      departureTerminalNo: reservation.departureTerminalNo,
      departureTime: reservation.departureTime,
      transportationCompanyCode: reservation.transportationCompanyCode,
      totalSeats: reservation.totalSeats,
      seats: reservation.seats.map(pick(['seatNo', 'ticketType'])),
    })
    return item.options
      .filter(option => option.isChosen)
      .map(option => {
        const reservations = option.reservations.map(convertReservation4Pay)
        return {
          type: 'bus',
          ...toBusType(reservations),
        }
      })
      .flat()
  }
}

type DomesticAccommodationCartItem = Extract<
  CartItemsV3Query['cartItemsV3'][number],
  { __typename?: 'DomesticAccommodationCartItem' }
>
export const isAvailableDomesticAccommodationOption = (
  option: DomesticAccommodationCartItem['options'][number]
) => {
  /**
   * 국내 숙소의 경우 remains 가 null 인 경우 재고 관리를 하지 않는 의미이므로 재고는 항상 있다고 판단한다
   */
  return option.availability && (option.remains ? option.remains > 0 : !option.outdated)
}
type DomesticLeisureCartItem = Extract<
  CartItemsV3Query['cartItemsV3'][number],
  { __typename?: 'DomesticLeisureCartItem' }
>
export const isAvailableDomesticLeisureOption = (
  option: DomesticLeisureCartItem['options'][number]
) => {
  /**
   * availability = true 인 경우
   *    outdated 항상 false
   *    quantityLimitPerOrder 항상 0 초과 혹은 null (무한대)
   *    remains BE 로직상으로 0 응답 가능 but, 주문시 오류 발생후 품절로 상품상태 변경됨
   *      따라서 remains 값만 별도로 체크 필요
   *
   * availability = false 인 경우
   *    항상 레저상품옵션 상태 disable
   */
  const isRemainInf = option.remains === null
  const isRemainBiggerThanZero = option.remains ? option.remains > 0 : false
  return option.availability && (isRemainInf || isRemainBiggerThanZero)
}
type DomesticTrainCartItem = Extract<
  CartItemsV3Query['cartItemsV3'][number],
  { __typename?: 'DomesticTrainCartItem' }
>
export const isAvailableDomesticTrainOption = (
  option: DomesticTrainCartItem['options'][number]
) => {
  /**
   * 국내 기차의 경우 remains 값은 없다
   */
  return option.availability && !option.outdated
}

export const isAvailableDomesticBusOption = (option: DomesticBusCartItemOption) => {
  return option.availability && !option.outdated
}

type OrderParam = {
  cgntId: string
  items: CartItemsV3Query['cartItemsV3']
  trackCode: string
  lng: string
  lat: string
}
export const convertCartItemsToOrder = (param: OrderParam) => {
  const { cgntId: cartId, trackCode, lng, lat, items } = param
  const filteredAvailableItems = items.map(item => {
    if (item.__typename === 'DomesticAccommodationCartItem') {
      return {
        ...item,
        options: item.options.filter(option => isAvailableDomesticAccommodationOption(option)),
      }
    } else if (item.__typename === 'DomesticLeisureCartItem') {
      return {
        ...item,
        options: item.options.filter(option => isAvailableDomesticLeisureOption(option)),
      }
    } else if (item.__typename === 'DomesticTrainCartItem') {
      return {
        ...item,
        options: item.options.filter(option => isAvailableDomesticTrainOption(option)),
      }
    } else if (item.__typename === 'DomesticBusCartItem') {
      return {
        ...item,
        options: item.options.filter(option => isAvailableDomesticBusOption(option)),
      }
    }
    return item
  })

  const options = filteredAvailableItems.map(item => item.options).flat()
  const selectedItems = options.filter(option => option.isChosen).map(option => option.id)
  const isCartSelectAll =
    Array.isArray(options) && options.length === selectedItems.length ? 'Y' : 'N'

  return {
    meta: {
      cartId,
      cgntId: cartId,
      trackCode,
      lng,
      lat,
      cartOptionIds: selectedItems, // 장바구니인 경우
      isCartSelectAll,
    },
    items: filteredAvailableItems.map(item => convertCategory(item)).flat(),
  }
}

// 재조회한 옵션들을 기존 선택한 장바구니에 업데이트한다
export const updateCartOptions = (
  cartItems: CartItemsV3Query['cartItemsV3'],
  updatedOptions: CartSingleItemsQuery['cartSingleItems'] = []
): CartItemsV3Query['cartItemsV3'] => {
  return cartItems.map(item => {
    const options = item.options.map(option => {
      const updatedOption = updatedOptions.find(updated => updated.option.id === option.id)
      return {
        ...(updatedOption?.option || option),
        isChosen: option.isChosen,
      }
    })

    return {
      ...item,
      options,
    }
  })
}

export const filterAvailableChosenOptions = (
  option: OrderParam['items'][number]['options'][number]
) => {
  if (option.__typename === 'DomesticAccommodationCartItemOption') {
    return option.isChosen && isAvailableDomesticAccommodationOption(option)
  } else if (option.__typename === 'DomesticLeisureCartItemOption') {
    return option.isChosen && isAvailableDomesticLeisureOption(option)
  } else if (option.__typename === 'DomesticTrainCartItemOption') {
    return option.isChosen && isAvailableDomesticTrainOption(option)
  } else if (option.__typename === 'DomesticBusCartItemOption') {
    return option.isChosen && isAvailableDomesticBusOption(option)
  }
  return option.isChosen
}
